import { provideHttpClient, withFetch, withInterceptors, withXsrfConfiguration } from '@angular/common/http';
import {
  APP_INITIALIZER,
  ApplicationConfig,
  ErrorHandler,
  importProvidersFrom,
  inject,
  provideZoneChangeDetection
} from '@angular/core';
import { provideNativeDateAdapter } from '@angular/material/core';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideRouter, withComponentInputBinding, withViewTransitions } from '@angular/router';

import { datadogLogs } from '@datadog/browser-logs';
import { datadogRum } from '@datadog/browser-rum';
import { NgxPermissionsModule } from 'ngx-permissions';

import { environment } from '../environments/environment';
import { routes } from './app.routes';
import { authInterceptor } from './core/interceptors/auth.interceptor';
import { AuthService } from './core/services/auth.service';
import { DevAuthService } from './core/services/dev-auth.service';
import { GlobalErrorHandler } from './core/services/error-handler.service';
import { PerformanceService } from './core/services/performance.service';

if (environment.datadog.clientToken) {
  datadogRum.init({
    applicationId: environment.datadog.applicationId,
    clientToken: environment.datadog.clientToken,
    site: environment.datadog.site,
    service: environment.datadog.service,
    env: environment.environmentName,
    sessionSampleRate: 100,
    sessionReplaySampleRate: environment.production ? 20 : 0,
    trackUserInteractions: true,
    trackResources: true,
    trackLongTasks: true,
    defaultPrivacyLevel: 'mask-user-input'
  });

  datadogLogs.init({
    clientToken: environment.datadog.clientToken,
    site: environment.datadog.site,
    service: environment.datadog.service,
    env: environment.environmentName,
    forwardErrorsToLogs: true,
    sessionSampleRate: 100
  });
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes, withComponentInputBinding(), withViewTransitions()),
    provideAnimationsAsync(),
    provideNativeDateAdapter(),
    provideHttpClient(
      withFetch(),
      withInterceptors([authInterceptor]),
      withXsrfConfiguration({ cookieName: 'XSRF-TOKEN', headerName: 'X-XSRF-TOKEN' })
    ),
    { provide: ErrorHandler, useClass: GlobalErrorHandler },
    importProvidersFrom(NgxPermissionsModule.forRoot()),
    {
      provide: APP_INITIALIZER,
      multi: true,
      useFactory: () => {
        const performanceService = inject(PerformanceService);
        return () => performanceService.init();
      }
    },
    // Dev-only: simulate the Company SSO cookie when running with mock data so
    // the rest of the app behaves identically in dev and production.
    {
      provide: APP_INITIALIZER,
      multi: true,
      useFactory: () => {
        const auth = inject(AuthService);
        const devAuth = inject(DevAuthService);
        return () => {
          if (!environment.production && environment.useMockData && !auth.isAuthenticated()) {
            devAuth.login();
          }
        };
      }
    }
  ]
};
